import React from "react";
import { motion } from "framer-motion";
import {
  Activity,
  Brain,
  Moon,
  Hammer,
  HeadphonesIcon,
  Clock,
  Hand,
  Move,
} from "lucide-react";

const symptoms = [
  {
    icon: Hammer,
    title: "Worn or chipped teeth",
    body: "Flattening of molars, hairline cracks, or crowns and fillings that keep failing.",
  },
  {
    icon: Brain,
    title: "Tension headaches",
    body: "Tight, band-like headaches around the temples — often worst on waking.",
  },
  {
    icon: Activity,
    title: "Jaw pain & soreness",
    body: "Aching, fatigued jaw muscles. Tender to press just below the cheekbone.",
  },
  {
    icon: Hand,
    title: "Jaw, cheek & neck tightness",
    body: "Knotted muscles around the jawline, cheeks, temples and neck — often achy throughout the day.",
  },
  {
    icon: Move,
    title: "Restricted mouth opening",
    body: "Jaw feels limited when opening wide, clicks or catches, or hurts when yawning, eating or smiling.",
  },
  {
    icon: Moon,
    title: "Disturbed sleep",
    body: "Partner-reported grinding noises, restless sleep, waking unrefreshed.",
  },
  {
    icon: HeadphonesIcon,
    title: "Earache or tinnitus",
    body: "Phantom earaches with no infection, or low ringing tied to jaw tension.",
  },
  {
    icon: Clock,
    title: "Square-jaw appearance",
    body: "Years of clenching can over-develop the jaw muscles, giving the lower face a wider, hardened shape.",
  },
];

export const Symptoms = () => {
  return (
    <section
      id="symptoms"
      data-testid="symptoms-section"
      className="py-24 lg:py-32 bg-rb-bg"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-12 gap-12 mb-16">
          <div className="lg:col-span-5">
            <div className="font-overline mb-4">The signs</div>
            <h2 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl text-rb-text leading-[1.1]">
              If three or more of these feel familiar,
              <span className="italic text-[#93C5FD]"> bruxism is likely</span>.
            </h2>
          </div>
          <div className="lg:col-span-6 lg:col-start-7 flex items-end">
            <p className="text-rb-text-2 text-base lg:text-lg leading-relaxed">
              Bruxism — the medical term for grinding and clenching — is rarely
              something you catch yourself doing. It plays out at night, or
              quietly during stressful moments in the day. The body keeps the
              score, though, and the symptoms often show up first.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {symptoms.map((s, i) => {
            const Icon = s.icon;
            return (
              <motion.div
                key={s.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.5, delay: i * 0.05 }}
                className="card-soft p-7"
                data-testid={`symptom-card-${i}`}
              >
                <div className="h-11 w-11 rounded-full bg-white/5 border border-white/10 flex items-center justify-center mb-5">
                  <Icon size={20} strokeWidth={1.5} className="text-[#93C5FD]" />
                </div>
                <h3 className="font-serif-display text-xl text-rb-text mb-2">
                  {s.title}
                </h3>
                <p className="text-sm text-rb-text-2 leading-relaxed">
                  {s.body}
                </p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};
